import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PublicNavBar from "../components/PublicNavBar";
import { Shield, ArrowRight, Mail, Lock } from "lucide-react";
import LinkedInButton from "../components/LinkedInButton";
import { api } from "../api";

/**
 * Login — unified, single-form sign-in (LinkedIn/Indeed pattern).
 *
 *  - No role selector: enter credentials, the server returns your role
 *    and we route accordingly. One form for Students, Companies and
 *    Institutes.
 *  - Admin accounts cannot sign in here. A separate, unadvertised
 *    `/admin-portal` route handles admin authentication.
 *  - All auth errors are returned as a generic message — we never
 *    disclose whether an email exists / has admin privileges.
 */
export default function Login() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const accent = "#006CFF";

  // `?sso_lab=<code>` means the user got here by clicking a partner-LMS
  // CoE card while logged out. After a successful login, kick off the
  // SSO handoff so they land on the partner already authenticated.
  const ssoLab = searchParams.get("sso_lab");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    // Server-side allow-list: this portal is for students, companies and
    // institutes only. Admin accounts are rejected here with a generic
    // "Invalid credentials" — they must use /admin-portal.
    const r = await login(email, password, ["student", "working_pro", "company", "institution"]);
    if (!r.ok) { setBusy(false); setError(r.error); return; }
    if (ssoLab) {
      try {
        const { data } = await api.post("/sso/issue-handoff", { lab_code: ssoLab });
        window.location.href = data.redirect_url;
        return;
      } catch (_) {
        // Fall through to the normal dashboard route if SSO fails —
        // student still gets a working session.
      }
    }
    setBusy(false);
    nav(`/${r.user.role}/dashboard`, { replace: true });
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <PublicNavBar />
      <div className="flex-1 flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="w-full max-w-md">
          <div
            className="bg-white border rounded-3xl shadow-[0_20px_60px_-30px_rgba(0,40,168,0.35)] overflow-hidden"
            style={{ borderColor: "var(--rule)" }}
          >
            {/* Hero */}
            <div
              className="px-6 sm:px-7 pt-6 sm:pt-7 pb-5"
              style={{
                background: `radial-gradient(120% 100% at 0% 0%, ${accent}1a 0%, transparent 60%), white`,
                borderBottom: `1px solid var(--rule)`,
              }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-10 h-10 rounded-2xl grid place-items-center"
                  style={{ background: `${accent}1a`, color: accent, boxShadow: `0 6px 16px -8px ${accent}80` }}
                >
                  <Shield size={20} strokeWidth={2.2} />
                </div>
                <span className="eyebrow" style={{ color: accent }}>Welcome back</span>
              </div>
              <h1 className="font-display font-black text-2xl sm:text-3xl mt-3 leading-tight" data-testid="login-heading">
                Sign in to <span style={{ color: accent }}>SkillConnect</span>
              </h1>
              <p className="text-sm text-muted mt-1.5">
                One account for students, working professionals, companies and institutes.
              </p>
            </div>

            {/* Body */}
            <div className="px-6 sm:px-7 pt-5 pb-7">
              <button
                onClick={onGoogle}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-2xl font-semibold text-sm border-2 bg-white transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] hover:shadow-[0_10px_24px_-12px_rgba(0,40,168,0.35)]"
                style={{ borderColor: accent, color: accent }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.2c0-.8-.1-1.6-.2-2.3H12v4.4h5.9c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-8.1z"/><path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2.1v2.8C3.9 20.6 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.7 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7.1H2.1C1.4 8.6 1 10.2 1 12s.4 3.4 1.1 4.9l3.6-2.8z"/><path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.6l3.1-3.1C17.4 2 14.9 1 12 1 7.7 1 3.9 3.4 2.1 7.1l3.6 2.8C6.6 7.4 9.1 5.4 12 5.4z"/></svg>
                Continue with Google
              </button>

              <div className="mt-3">
                <LinkedInButton testId="linkedin-login-btn" />
              </div>

              <div className="flex items-center gap-3 my-5 text-[10px] uppercase tracking-widest text-muted">
                <span className="h-px flex-1 bg-rule" /> or email <span className="h-px flex-1 bg-rule" />
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="group">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted" htmlFor="email">Email</label>
                  <div className="relative mt-1.5">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                    <input
                      id="email" type="email" required autoFocus autoComplete="email"
                      className="input input-with-icon"
                      value={email} onChange={(e)=>setEmail(e.target.value)}
                      data-testid="login-email-input"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div className="group">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted" htmlFor="password">Password</label>
                  <div className="relative mt-1.5">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                    <input
                      id="password" type="password" required autoComplete="current-password"
                      className="input input-with-icon"
                      value={password} onChange={(e)=>setPassword(e.target.value)}
                      data-testid="login-password-input"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                {error && (
                  <div
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 animate-[shake_0.4s_ease]"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit" disabled={busy}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl font-bold text-sm text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(0,40,168,0.55)] active:translate-y-0 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                  style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)` }}
                  data-testid="login-submit-btn"
                >
                  {busy ? (
                    <>
                      <span className="inline-block w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>Sign in <ArrowRight size={15} /></>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-muted">
                New to SkillConnect?{" "}
                <Link
                  to="/signup"
                  className="font-semibold hover:underline transition-colors"
                  style={{ color: accent }}
                  data-testid="goto-signup"
                >
                  Create an account
                </Link>
              </p>
            </div>
          </div>

          <p className="text-center text-[11px] text-muted mt-4">
            Trouble signing in? <a href="mailto:info@skillconnect.com" className="underline">Contact support</a>
          </p>

          <p className="text-center text-[11px] text-muted mt-3" data-testid="login-techcatalyst-credit">
            A product by{" "}
            <a
              href="https://techcatalyst.world/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: "var(--ink)" }}
            >
              Tech Catalyst Private Limited
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
